import { Badge, Collapse, Typography } from 'antd'
import { useComments } from 'features/libra/comments/CommentsProvider'
import { niiReportConfigSchema } from 'features/libra/stepComponents/niiReport/niiReportApi'
import { NiiPdfExportButton } from 'features/libra/stepComponents/niiReport/pdf/NiiPdfExportButton'
import { useReportValidation } from 'features/libra/StepValidationContext'
import { ActiveStepIdOverride } from 'features/libra/ticket/TicketStateContext'
import {
  LibraWorflowStepComponentProps,
  LibraWorkflowStep,
  StepValidationResult,
} from 'features/libra/workflow/workflowStepsApi'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { Dispatch, FC, SetStateAction, useMemo } from 'react'
import styled from 'styled-components'
import { z } from 'zod'

import { Component as ConditionalFormComponent } from './ConditionalForm'
import { NiiReportReviewPanel } from './niiReview/NiiReportReviewPanel'
import { ZoneAnalysisReviewPanel } from './niiReview/ZoneAnalysisReviewPanel'
import { Component as NiiScreeningComponent } from './NiiScreening'

const NiiReviewConfigSchema = z.object({
  stepSequence: z.array(z.string()),
})

type NiiReviewConfig = z.infer<typeof NiiReviewConfigSchema>

type ReadonlyStepComponent = FC<{
  config: Record<string, unknown>
  name: string
  setValidationResult: Dispatch<SetStateAction<StepValidationResult>>
  readonly?: boolean
}>

/**
 * Components that support the `readonly` prop natively.
 * These are rendered directly with readonly={true} instead of using a dedicated review panel.
 */
const READONLY_STEP_COMPONENTS: Record<string, ReadonlyStepComponent> = {
  // Safe to cast: step configs extend Record<string, unknown> and are validated by each component
  ConditionalForm: ConditionalFormComponent as ReadonlyStepComponent,
  NiiScreening: NiiScreeningComponent as ReadonlyStepComponent,
}

/**
 * Components that use dedicated readonly review panels (because the step
 * component's editing UI is too deeply coupled to make a simple readonly prop practical).
 */
const REVIEW_PANEL_RENDERERS: Record<
  string,
  FC<{ config: Record<string, unknown> }>
> = {
  ZoneAnalysis: ZoneAnalysisReviewPanel,
  NiiReport: NiiReportReviewPanel,
}

const noopSetValidationResult: Dispatch<
  SetStateAction<StepValidationResult>
> = () => {}

const StepReviewPanel: FC<{ step: LibraWorkflowStep }> = ({ step }) => {
  const ReadonlyComponent = READONLY_STEP_COMPONENTS[step.component]
  if (ReadonlyComponent) {
    return (
      <ActiveStepIdOverride stepId={step.id}>
        <ReadonlyComponent
          config={step.config}
          name={step.name}
          setValidationResult={noopSetValidationResult}
          readonly
        />
      </ActiveStepIdOverride>
    )
  }

  const ReviewPanel = REVIEW_PANEL_RENDERERS[step.component]
  if (ReviewPanel) {
    return (
      <ActiveStepIdOverride stepId={step.id}>
        <ReviewPanel config={step.config} />
      </ActiveStepIdOverride>
    )
  }

  return (
    <UnsupportedText>Review not available for this step type.</UnsupportedText>
  )
}

export const Component: FC<LibraWorflowStepComponentProps<NiiReviewConfig>> = ({
  config,
}) => {
  const workflow = useWorkflow()

  const parsedConfig = useMemo(
    () => NiiReviewConfigSchema.parse(config),
    [config]
  )

  // Review step is always valid - nothing to edit
  useReportValidation(undefined)

  const reviewSteps = useMemo(() => {
    return parsedConfig.stepSequence
      .map((stepId) => workflow.steps.find((s) => s.id === stepId))
      .filter((s): s is LibraWorkflowStep => s !== undefined)
  }, [parsedConfig.stepSequence, workflow.steps])

  const { getUnresolvedCountForStep } = useComments()

  const collapseItems = useMemo(
    () =>
      reviewSteps.map((step) => {
        const unresolvedCount = getUnresolvedCountForStep(step.id)
        return {
          key: step.id,
          label: (
            <PanelLabel>
              {step.name}
              {unresolvedCount > 0 && (
                <Badge
                  count={unresolvedCount}
                  size='small'
                  style={{ marginLeft: 8 }}
                />
              )}
            </PanelLabel>
          ),
          children: <StepReviewPanel step={step} />,
        }
      }),
    [reviewSteps, getUnresolvedCountForStep]
  )

  const niiReportConfig = useMemo(() => {
    const reportStep = reviewSteps.find((s) => s.component === 'NiiReport')
    if (!reportStep) return undefined
    const parsed = niiReportConfigSchema.safeParse(reportStep.config)
    return parsed.success ? parsed.data : undefined
  }, [reviewSteps])

  return (
    <Container>
      <Typography.Text type='secondary'>
        Readonly summary of all steps. Review the collected data before
        completing the workflow.
      </Typography.Text>
      <StyledCollapse items={collapseItems} bordered={false} />
      {niiReportConfig && (
        <NiiPdfExportButton
          fields={niiReportConfig.fields}
          zoneFieldName={niiReportConfig.zoneFieldName}
        />
      )}
    </Container>
  )
}

export const useValidateConfig = ({
  config,
}: {
  config: Record<string, unknown>
  name: string
}) => {
  return useMemo(
    () => NiiReviewConfigSchema.safeParse(config).success,
    [config]
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`

const StyledCollapse = styled(Collapse)`
  .ant-collapse-header {
    padding: 12px 16px !important;
    font-weight: 600;
  }

  .ant-collapse-content-box {
    padding: 12px 16px !important;
  }
`

const PanelLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
`

const UnsupportedText = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-style: italic;
  padding: 0.5rem 0;
`
